import { useCallback, useMemo, useState, type ReactElement } from 'react'
import { ButtonNavigate, ButtonSave, Page } from '@components'
import { usePermissionCheck, useUpdateGame, type TinyBracketGame, useTranslation } from '@front/model'
import { useToastError } from '@hooks'
import { type BracketGameData, isEmpty, isNotDefined } from '@terros/common'
import { CompetitionPageContainer } from '../competition/CompetitionPageContainer'
import { ActionNeeded } from '../competition_.$gameId/ActionNeeded'
import { useGameContext } from '../competition_.$gameId/useGameContext'
import { DisplayBracket } from './components'
import { roundsHaveSameParticipants } from './util'
import { createFileRoute } from '@tanstack/react-router'

function BracketPage(): ReactElement {
  const { t } = useTranslation()
  const { data, ...state } = useGameContext()
  const { data: canEdit } = usePermissionCheck({
    permissionType: 'game:save',
    targetId: [data.game.ownerId, data.game.companyId],
  })

  const { update, isUpdating, error } = useUpdateGame()
  useToastError(error)

  if (data.game.gameType !== 'bracket') throw new Error('Game is not a bracket')
  const [game, setGame] = useState<TinyBracketGame>(data.game)

  const save = useCallback(async () => {
    const updatedGame = await update({ ...data.game, ...game })
    if (updatedGame.gameType !== 'bracket') throw new Error('Game is not a bracket')
    setGame(updatedGame)
  }, [update, data.game, game])

  const hasNotChanged = useMemo(
    () => data.game.gameType === 'bracket' && roundsHaveSameParticipants(data.game.rounds, game.rounds),
    [data.game, game]
  )

  if (shouldWarn(game))
    return (
      <ActionNeeded message={t('The bracket needs to be setup before you can view it.')} title={t('Bracket not setup')}>
        <ButtonNavigate to={`/competition/${game.gameId}/timeline`} variant='secondary'>
          {t('Setup Bracket')}
        </ButtonNavigate>
      </ActionNeeded>
    )
  return (
    <CompetitionPageContainer>
      <Page.Heading {...state} title={t('Bracket')}>
        <ButtonSave disabled={hasNotChanged} loading={isUpdating} onClick={save} />
      </Page.Heading>
      <Page.Content scrollArea>
        <DisplayBracket game={game} participants={data.participants} setGame={canEdit ? setGame : undefined} />
      </Page.Content>
    </CompetitionPageContainer>
  )
}

const shouldWarn = (game: Pick<BracketGameData, 'rounds' | 'settings'>): boolean => {
  return isNotDefined(game.settings.seedingType) && isEmpty(game.rounds.flatMap((r) => r.matches))
}

export const Route = createFileRoute('/competition_/$gameId/bracket/')({
  component: BracketPage,
})
